import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, NEVER, Observable } from 'rxjs';
import { catchError, map, retry, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { BaseContainer, NavigationLink } from '@stylehaven/components';
import { NotificationEntity } from '../../../entities/notifications/notification.entity';
import { PaginatedResults } from '../../../record/entities/pagination/paginated-results.entity';
import { NotificationsSandboxService } from '../../sandbox/notifications-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-notification-dashboard',
    templateUrl: './notification-dashboard-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationDashboardContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly refresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    readonly COLUMN_COUNT: number = 4;

    notificationPage$!: Observable<PaginatedResults<NotificationEntity>>;
    notifications$!: Observable<NotificationEntity[]>;
    totalItems$!: Observable<number>;

    constructor(
        private readonly sandbox: NotificationsSandboxService,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.initSourceStreams();
        this.initPresentationalStreams();
    }

    ngOnDestroy(): void {
        this.refresh$.complete();
    }

    getNavigationLink(notification: NotificationEntity): NavigationLink {
        const path = ['records', notification.Action, 'head'];
        return {
            route: this.router.createUrlTree(path).toString()
        };
    }

    deleteNotification(notification: NotificationEntity): void {
        this.sandbox
            .deleteNotification(notification)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.refresh$.next(true));
    }

    private initSourceStreams(): void {
        this.notificationPage$ = this.refresh$.pipe(
            tap(() => this.loading$.startLoading()),
            switchMap(() =>
                this.sandbox
                    .getNotifications({
                        startIndex: 0,
                        nrOfResults: 5
                    })
                    .pipe(retry(1), take(1), takeUntil(this.destroy$))
            ),
            catchError(() => {
                this.error$.next(true);
                this.loading$.stopLoading();
                return NEVER;
            }),
            tap(() => this.loading$.stopLoading()),
            takeUntil(this.destroy$)
        );

        this.notifications$ = this.notificationPage$.pipe(
            map(result => result.results),
            take(1),
            takeUntil(this.destroy$)
        );
    }

    private initPresentationalStreams(): void {
        this.totalItems$ = this.notificationPage$.pipe(
            map(page => page.paginationInfo.totalNumberOfResults),
            takeUntil(this.destroy$)
        );
    }

    get navigationLink(): NavigationLink {
        return { route: '/notifications' };
    }
}
